// "Ask about this" (JEV.md §4) as a sheet over the reader: narration pauses, the question is typed (or spoken, where
// the browser has speech recognition), the answer appears (and is read aloud), passages jump back into the text.
import { ask, history, speak, stopSpeaking, type AskRecord } from "../ask/ask";
import type { Article } from "../model/article";
import { player } from "../narration/player";
import { settings } from "../settings";
import { clear, h, icon } from "./dom";
import { sheet } from "./library";

export function openAsk(a: Article, paragraph: number, onJump: (sourceParagraph: number) => void) {
  if (!settings.askEnabled) { alert("Ask about this is off (Settings → Ask about this)."); return; }
  const wasPlaying = player.articleId === a.id && player.isActive;
  if (wasPlaying) player.pause();
  let ctrl: AbortController | null = null;
  const out = h("div.ask-out");
  const input = h("input.ask-input", { placeholder: "Ask about this article…", "aria-label": "Your question", autocomplete: "off", dataset: { test: "ask-input" } });
  const form = h("form.ask-form", { onsubmit: (e: Event) => { e.preventDefault(); submit(); } }, input,
    ("webkitSpeechRecognition" in window || "SpeechRecognition" in window) ? h("button.icon-btn", { type: "button", "aria-label": "Speak your question", onclick: () => listen() }, icon("mic")) : null,
    h("button.btn.primary", { type: "submit", dataset: { test: "ask-submit" } }, "Ask"));
  const past = h("div.ask-history");
  const body = h("div.sheet-body.ask", null, form, out, past);

  const show = (r: AskRecord, into: HTMLElement) => {
    into.append(h("div.ask-q", null, r.question), h("div.ask-a", { dataset: { test: "ask-answer" } }, r.answer),
      h("div.ask-passages", null, ...r.passages.map((p) => h("button.passage", { onclick: () => { close(); onJump(p.source); } }, h("span.meta", null, `¶ ${p.source + 1}`), p.preview))),
      r.by ? h("div.meta", null, `Passages found by ${r.by}`) : "");
  };

  const drawHistory = async () => {
    const all = await history(a.id);
    clear(past);
    if (all.length) past.append(h("h3.group", null, "Earlier questions"));
    for (const r of [...all].reverse().slice(1)) { const d = h("details.ask-old", null, h("summary", null, r.question)); show(r, d); past.append(d); }
  };

  const submit = async () => {
    const q = input.value.trim();
    if (!q) return;
    ctrl?.abort();
    ctrl = new AbortController();
    const signal = ctrl.signal;
    clear(out);
    const phase = h("div.ask-phase", null, h("span.pulse"), "Finding the passages…");
    out.append(phase);
    try {
      const r = await ask(a, q, paragraph, signal, (p) => { phase.lastChild!.textContent = p === "retrieving" ? "Finding the passages…" : p === "answering" ? "Answering…" : "Speaking…"; });
      if (signal.aborted) return;
      clear(out);
      show(r, out);
      input.value = "";
      drawHistory();
      if (settings.askSpeak) await speak(a, r.answer, signal);
      if (!signal.aborted && wasPlaying && settings.askResume) player.resume();
    } catch (e) {
      if (signal.aborted) return;
      clear(out);
      out.append(h("div.error", null, "Couldn't get an answer (" + (e instanceof Error ? e.message : String(e)) + ")."));
    }
  };

  const listen = () => {
    const R = (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition;
    if (!R) return;
    const rec = new R();
    rec.lang = "en-US"; rec.interimResults = true;
    rec.onresult = (e: any) => { input.value = Array.from(e.results as ArrayLike<any>).map((x: any) => x[0].transcript).join(""); if (e.results[e.results.length - 1].isFinal) submit(); };
    rec.onerror = () => { input.placeholder = "Couldn't listen — type your question instead."; };
    rec.start();
  };

  const close = sheet("Ask about this", body, [], () => { ctrl?.abort(); stopSpeaking(); });
  drawHistory();
  setTimeout(() => input.focus(), 50);
}
